import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  CheckCircle2,
  MapPin,
  ChevronRight,
  Home,
  CloudLightning,
  LayoutPanelLeft,
  AppWindow,
  DoorOpen,
  Waves,
  Hammer,
} from "lucide-react";
import { company, services, serviceAreas, getServiceArea } from "../mock";
import { comboServiceSlugs } from "../comboPages";
import ContactForm from "../components/ContactForm";
import FAQ from "../components/FAQ";
import JsonLd from "../components/JsonLd";
import useSEO from "../hooks/useSEO";
import { BIZ_REF, placeName, LANDING_BY_CITY } from "../seo";

const iconMap = {
  Home,
  CloudLightning,
  LayoutPanelLeft,
  AppWindow,
  DoorOpen,
  Waves,
  Hammer,
};

const trustBadges = [
  "Licensed & Insured",
  "Free Roof Inspections",
  "24/7 Storm Response",
  "GAF Master Elite",
];

const CityServiceArea = () => {
  const { slug } = useParams();
  const area = getServiceArea(slug);

  const place = area ? placeName(area) : "";
  const landing = area && LANDING_BY_CITY[area.slug];
  useSEO({
    title: area && (landing
      ? `Roofing, Siding & Gutters in ${place} | Fab Build Pro`
      : `Roofing Contractor ${place} | Fab Build Pro`),
    description: area && `Roof repair, replacement & storm damage repair in ${place}, plus siding, windows & gutters. Licensed, GAF Master Elite. Call ${company.phone}.`,
  });

  if (!area) return <Navigate to="/service-areas" replace />;

  const nearby = serviceAreas.filter((a) => a.slug !== area.slug).slice(0, 6);
  const primaryZip = area.zips[0];

  const cityFaqs = [
    {
      q: `Does Fab Build Pro provide roofing services in ${area.name}?`,
      a: `Yes — Fab Build Pro serves ${area.name} (${area.zips.join(", ")}) and the surrounding ${area.region} with roof repair, replacement, storm damage restoration, siding, windows, gutters and doors. Call ${company.phone} for a free estimate.`,
    },
    {
      q: `Do you offer free roof inspections in ${area.name}?`,
      a: `Absolutely. We provide free, no-obligation roof inspections and written estimates throughout ${area.name} and can typically schedule within 24–48 hours.`,
    },
    {
      q: `Can you help with storm damage insurance claims in ${area.name}?`,
      a: `Yes. We are storm-damage specialists and handle the insurance process end to end for ${area.name} homeowners. Many qualify to replace their roof with little to no out-of-pocket cost.`,
    },
    {
      q: `Is Fab Build Pro licensed and insured to work in ${area.name}?`,
      a: `Yes. We are fully licensed and insured and are a GAF Master Elite certified contractor serving ${area.region}.`,
    },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://fabbuildpro.com/" },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: "https://fabbuildpro.com/service-areas" },
      { "@type": "ListItem", position: 3, name: area.name, item: `https://fabbuildpro.com/service-areas/${area.slug}` },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roofing & Home Improvement",
    provider: BIZ_REF,
    areaServed: { "@type": "Place", name: place },
    description: `Roofing, storm damage repair, siding, windows, gutters and remodeling in ${area.name} (${area.zips.join(", ")}).`,
  };

  const whyPoints = [
    `Local crews that know ${area.name} and ${area.region}`,
    "Free, no-obligation roof inspections & estimates",
    "We handle insurance claims from start to finish",
    "Licensed, insured & manufacturer-certified installers",
    "Fast response — often within 24–48 hours",
    "Backed by our 100% satisfaction guarantee",
  ];

  return (
    <>
      <JsonLd data={breadcrumbSchema} id="city-breadcrumb" />
      <JsonLd data={serviceSchema} id="city-service" />
      {/* Hero */}
      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-slate to-brand-navy" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-20 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-white/60 mb-4">
            <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/service-areas" className="hover:text-brand-orange transition-colors">Service Areas</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-brand-orange">{area.name}</span>
          </div>
          <p className="text-brand-orange font-semibold tracking-[0.18em] text-sm uppercase mb-3">
            {area.region}
          </p>
          <h1 className="font-display font-extrabold text-white text-3xl md:text-5xl leading-tight">
            Roofing & Home Improvement in {place}
          </h1>
          <p className="text-white/75 text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
            Fab Build Pro proudly serves {area.name} and the surrounding{" "}
            {area.region} community. From roof repairs and full replacements to
            siding, windows, gutters and remodeling — we bring quality
            craftsmanship right to your front door.
          </p>
          {landing && (
            <Link to={landing} data-testid="city-landing-link" className="inline-flex items-center gap-2 mt-4 text-brand-orange font-semibold hover:gap-3 transition-all">
              Need a roofer? See our {area.name} roof repair page <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a
              href={company.phoneHref}
              className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" /> Call {company.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all"
            >
              Get Free Estimate <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8">
            {trustBadges.map((b) => (
              <div key={b} className="flex items-center gap-2 text-white/85 text-sm">
                <CheckCircle2 className="w-5 h-5 text-brand-orange" /> {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-5">
            Trusted Roofing Contractor for {area.name} Homeowners
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p>
              Choosing a roofing and general contractor in {area.name} is an
              important decision. Our local team understands the {area.region}{" "}
              climate — from summer storms to winter freezes — and delivers
              durable, professional workmanship tailored to every home.
            </p>
            <p>
              Whether you're dealing with a sudden leak, storm or hail damage,
              or planning a long-overdue upgrade, one quick call connects you
              with certified roofers, siding specialists and remodelers serving
              {" "}
              {area.name}{" "}
              (including {area.zips.join(", ")}). You may even qualify to
              replace your roof with little or no out-of-pocket cost through your
              insurance.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-brand-sand py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl">
              Our Services in {area.name}
            </h2>
            <div className="w-20 h-1 bg-brand-orange rounded-full mx-auto mt-4" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Home;
              const to = comboServiceSlugs.includes(s.slug)
                ? `/services/${s.slug}/${area.slug}`
                : `/services/${s.slug}`;
              return (
                <Link
                  to={to}
                  key={s.slug}
                  className="group bg-white rounded-2xl p-6 border border-black/5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-navy group-hover:bg-brand-orange flex items-center justify-center shrink-0 transition-colors">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-brand-navy">
                      {s.title}
                    </h3>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                      {s.short}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl text-center mb-10">
            Why {area.name} Homeowners Choose Fab Build Pro
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {whyPoints.map((p) => (
              <div
                key={p}
                className="flex items-start gap-3 bg-white rounded-xl p-4 border border-black/5 shadow-sm"
              >
                <CheckCircle2 className="w-6 h-6 text-brand-orange shrink-0" />
                <span className="text-slate-700">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead form */}
      <ContactForm />

      {/* City FAQ */}
      <FAQ
        heading={`${area.name} Roofing FAQs`}
        items={cityFaqs}
      />

      {/* Nearby areas */}
      <section className="bg-brand-sand py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-8">
            Nearby Areas We Serve
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to={`/service-areas/${n.slug}`}
                className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                {n.name}
              </Link>
            ))}
          </div>
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 mt-8 text-brand-orange font-semibold hover:gap-3 transition-all"
          >
            View All Service Areas <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-brand-navy py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-white text-2xl md:text-3xl mb-4">
            Serving {place} {primaryZip && `· ${primaryZip}`}
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Speak with our local team about your roofing or home improvement
            project. Free inspections and honest, upfront pricing.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={company.phoneHref}
              className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" /> Call {company.phone}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all"
            >
              Request a Free Estimate <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CityServiceArea;
